/* eslint-disable prettier/prettier */
/* EQUIPAMENTOS_MODULE_V3_GESTAO */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box, Typography, Button, TextField, Select, MenuItem, FormControl, InputLabel,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Checkbox, IconButton, Tooltip, Chip, CircularProgress, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, Grid,
  Tabs, Tab, Divider, ListItemText,
} from '@mui/material';
import {
  Add, Edit, Delete, Search, Person, Settings as SettingsIcon,
  ArrowDownward, Business, LinkOff, RestoreFromTrash, DeleteSweep,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const STATUS_OPTIONS = ['Ativo', 'Inativo', 'Em Manutenção', 'Aguardando Peça'];

const EMPTY_FORM = {
  entidade: '',
  zona: '',
  unidade: '',
  piso: '',
  local: '',
  numero_local: '',
  codigo_bim: '',
  nome: '',
  tipo_equipamento: '',
  familia: '',
  sistema: '',
  subsistema: '',
  marca: '',
  modelo: '',
  numero_serie: '',
  status: 'Ativo',
  observacoes: '',
  cliente_ids: [],
};

const statusColor = (s) => {
  if (s === 'Ativo') return 'success';
  if (s === 'Inativo') return 'default';
  if (s === 'Em Manutenção') return 'warning';
  return 'info';
};

const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
};

export default function Equipamentos() {
  const { token, isAdmin } = useAuth();
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const [equipamentos, setEquipamentos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [filterOpts, setFilterOpts] = useState({ pisos: [], locais: [], tipos: [], unidades: [] });
  const [filters, setFilters] = useState({
    cliente_id: '', piso: '', local: '', tipo_equipamento: '', status: '', q: '',
  });
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const [batchDialog, setBatchDialog] = useState(null);
  const [batchValue, setBatchValue] = useState('');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('todos');

  const [deletedEquipamentos, setDeletedEquipamentos] = useState([]);
  const [loadingDeleted, setLoadingDeleted] = useState(false);
  const [selectedDeleted, setSelectedDeleted] = useState([]);

  const fetchEquipamentos = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
      const res = await fetch(`/api/equipamentos?${params}`, { headers });
      if (!res.ok) throw new Error('Erro ao carregar equipamentos.');
      const data = await res.json();
      setEquipamentos(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [filters, token]);

  const fetchDeleted = useCallback(async () => {
    setLoadingDeleted(true);
    try {
      const res = await fetch('/api/equipamentos/deleted', { headers });
      if (!res.ok) throw new Error('Erro ao carregar excluídos.');
      const data = await res.json();
      setDeletedEquipamentos(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoadingDeleted(false);
    }
  }, [token]);

  useEffect(() => { fetchEquipamentos(); }, [fetchEquipamentos]);

  useEffect(() => {
    Promise.all([
      fetch('/api/equipamentos/filters', { headers }).then(r => r.ok ? r.json() : {}).catch(() => ({})),
      fetch('/api/clientes', { headers }).then(r => r.ok ? r.json() : []).catch(() => []),
    ]).then(([opts, cls]) => {
      setFilterOpts(opts || {});
      setClientes(Array.isArray(cls) ? cls : []);
    });
  }, [token]);

  useEffect(() => {
    if (activeTab === 'excluidos') fetchDeleted();
  }, [activeTab]);

  const clientesComEquipamentos = useMemo(() => {
    const ids = new Set(
      equipamentos.flatMap(e => Array.isArray(e.cliente_ids) ? e.cliente_ids.map(String) : [])
    );
    return clientes.filter(c => ids.has(String(c.id)));
  }, [equipamentos, clientes]);

  const displayEquipamentos = useMemo(() => {
    if (activeTab === 'todos' || activeTab === 'excluidos') return equipamentos;
    return equipamentos.filter(e =>
      Array.isArray(e.cliente_ids) && e.cliente_ids.map(String).includes(String(activeTab))
    );
  }, [activeTab, equipamentos]);

  const activeCliente = useMemo(() =>
    clientes.find(c => String(c.id) === activeTab) || null,
  [activeTab, clientes]);

  const selectedCanDelete = useMemo(() =>
    selected.filter(id => {
      const eq = equipamentos.find(e => e.id === id);
      return !eq?.cliente_ids?.length;
    }),
  [selected, equipamentos]);

  const selectedBlocked = useMemo(() =>
    selected.map(id => equipamentos.find(e => e.id === id)).filter(e => e?.cliente_ids?.length > 0),
  [selected, equipamentos]);

  const handleSelectAll = (e) => {
    setSelected(e.target.checked ? displayEquipamentos.map(eq => eq.id) : []);
  };
  const handleSelect = (id) => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const handleSelectDeleted = (id) => {
    setSelectedDeleted(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const openAdd = () => {
    setEditId(null);
    setFormData({ ...EMPTY_FORM, cliente_ids: activeTab !== 'todos' && activeTab !== 'excluidos' ? [Number(activeTab)] : [] });
    setModalOpen(true);
  };

  const openEdit = (eq) => {
    setEditId(eq.id);
    setFormData({
      entidade: eq.entidade ?? '',
      zona: eq.zona ?? '',
      unidade: eq.unidade ?? '',
      piso: eq.piso ?? '',
      local: eq.local ?? '',
      numero_local: eq.numero_local ?? '',
      codigo_bim: eq.codigo_bim ?? '',
      nome: eq.nome ?? '',
      tipo_equipamento: eq.tipo_equipamento ?? '',
      familia: eq.familia ?? '',
      sistema: eq.sistema ?? '',
      subsistema: eq.subsistema ?? '',
      marca: eq.marca ?? '',
      modelo: eq.modelo ?? '',
      numero_serie: eq.numero_serie ?? '',
      status: eq.status ?? 'Ativo',
      observacoes: eq.observacoes ?? '',
      cliente_ids: Array.isArray(eq.cliente_ids) ? eq.cliente_ids.map(Number) : [],
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!formData.nome.trim()) { setError('Nome é obrigatório.'); return; }
    setSaving(true);
    setError(null);
    try {
      const { cliente_ids, ...equipamentoFields } = formData;
      const url = editId ? `/api/equipamentos/${editId}` : '/api/equipamentos';
      const res = await fetch(url, {
        method: editId ? 'PUT' : 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ equipamento: equipamentoFields, cliente_ids }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body.errors || []).join(', ') || 'Erro ao salvar.');
      }
      setModalOpen(false);
      setSelected([]);
      fetchEquipamentos();
      fetch('/api/equipamentos/filters', { headers }).then(r => r.json()).then(setFilterOpts).catch(() => {});
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSingle = async (id) => {
    if (activeTab !== 'todos') {
      if (!window.confirm('Remover este equipamento do cliente? (O equipamento continua em "Todos")')) return;
      const res = await fetch(`/api/equipamentos/${id}/remove_cliente`, {
        method: 'DELETE',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ cliente_id: activeTab }),
      });
      if (res.ok) { fetchEquipamentos(); setSelected(prev => prev.filter(x => x !== id)); }
    } else {
      if (!window.confirm('Mover este equipamento para a lixeira? Poderá ser restaurado depois.')) return;
      const res = await fetch(`/api/equipamentos/${id}`, { method: 'DELETE', headers });
      if (res.ok) { fetchEquipamentos(); setSelected(prev => prev.filter(x => x !== id)); }
    }
  };

  const handleBatchUpdate = async () => {
    const body = { ids: selected };
    if (batchDialog === 'cliente') body.add_cliente_id = batchValue ? Number(batchValue) : 0;
    if (batchDialog === 'status') body.status = batchValue;
    await fetch('/api/equipamentos/batch_update', {
      method: 'PATCH',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    setBatchDialog(null);
    setBatchValue('');
    setSelected([]);
    fetchEquipamentos();
  };

  const handleBatchDelete = async () => {
    if (activeTab !== 'todos') {
      await fetch('/api/equipamentos/batch_update', {
        method: 'PATCH',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: selected, remove_cliente_id: activeTab }),
      });
    } else {
      if (selectedCanDelete.length === 0) { setDeleteOpen(false); return; }
      await fetch('/api/equipamentos/batch_destroy', {
        method: 'DELETE',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: selectedCanDelete }),
      });
    }
    setDeleteOpen(false);
    setSelected([]);
    fetchEquipamentos();
  };

  const handleRestoreSingle = async (id) => {
    await fetch('/api/equipamentos/restore', {
      method: 'PATCH',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids: [id] }),
    });
    fetchDeleted();
    fetchEquipamentos();
  };

  const handleRestoreBatch = async () => {
    await fetch('/api/equipamentos/restore', {
      method: 'PATCH',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids: selectedDeleted }),
    });
    setSelectedDeleted([]);
    fetchDeleted();
    fetchEquipamentos();
  };

  const setField = (key) => (e) => setFormData(f => ({ ...f, [key]: e.target.value }));

  const FORM_FIELDS = [
    { label: 'Entidade', key: 'entidade', sm: 4 },
    { label: 'Zona', key: 'zona', sm: 4 },
    { label: 'Unidade', key: 'unidade', sm: 4 },
    { label: 'Piso', key: 'piso', sm: 4 },
    { label: 'Local', key: 'local', sm: 4 },
    { label: 'Nº Local', key: 'numero_local', sm: 4 },
    { label: 'Código BIM', key: 'codigo_bim', sm: 4 },
    { label: 'Nome do Equipamento *', key: 'nome', sm: 8 },
    { label: 'Tipo de Equipamento', key: 'tipo_equipamento', sm: 6 },
    { label: 'Família', key: 'familia', sm: 6 },
    { label: 'Sistema', key: 'sistema', sm: 6 },
    { label: 'Subsistema', key: 'subsistema', sm: 6 },
    { label: 'Marca', key: 'marca', sm: 4 },
    { label: 'Modelo', key: 'modelo', sm: 4 },
    { label: 'Nº de Série', key: 'numero_serie', sm: 4 },
  ];

  const isExcluidos = activeTab === 'excluidos';

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5" fontWeight={700}>Equipamentos</Typography>
        {!isExcluidos && (
          <Button variant="contained" startIcon={<Add />} onClick={openAdd}>
            Adicionar Equipamento
          </Button>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Abas */}
      <Paper sx={{ mb: 2 }} elevation={0} variant="outlined">
        <Tabs
          value={activeTab}
          onChange={(_, v) => { setActiveTab(v); setSelected([]); setSelectedDeleted([]); }}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ minHeight: 44 }}
        >
          <Tab label="Todos" value="todos" sx={{ minHeight: 44, fontWeight: activeTab === 'todos' ? 700 : 400 }} />
          {clientesComEquipamentos.map(c => (
            <Tab
              key={c.id}
              value={String(c.id)}
              icon={<Business sx={{ fontSize: 15 }} />}
              iconPosition="start"
              label={c.nome}
              sx={{ minHeight: 44, fontWeight: activeTab === String(c.id) ? 700 : 400 }}
            />
          ))}
          <Tab
            value="excluidos"
            icon={<DeleteSweep sx={{ fontSize: 15 }} />}
            iconPosition="start"
            label={`Excluídos${deletedEquipamentos.length > 0 ? ` (${deletedEquipamentos.length})` : ''}`}
            sx={{ minHeight: 44, ml: 'auto', color: isExcluidos ? 'error.main' : 'text.secondary', fontWeight: isExcluidos ? 700 : 400, '&.Mui-selected': { color: 'error.main' } }}
          />
        </Tabs>
      </Paper>

      {/* Indicador de cliente ativo */}
      {activeCliente && (
        <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
          <Chip icon={<Business />} label={activeCliente.nome} color="primary" sx={{ fontWeight: 700, fontSize: 13, height: 32 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', ml: 1.5, color: 'primary.main' }}>
            <ArrowDownward sx={{ fontSize: 18 }} />
            <Typography variant="caption" sx={{ ml: 0.5, fontWeight: 600 }}>
              {displayEquipamentos.length} equipamento(s) deste cliente
            </Typography>
          </Box>
          <Divider sx={{ width: '100%', mt: 0.5 }} />
        </Box>
      )}

      {/* ══ ABA EXCLUÍDOS ══ */}
      {isExcluidos && (
        <>
          {selectedDeleted.length > 0 && (
            <Paper sx={{ p: 1.5, mb: 2, bgcolor: '#fff3e0', display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Typography fontWeight={600} color="warning.dark">{selectedDeleted.length} selecionado(s)</Typography>
              <Button size="small" variant="outlined" color="warning" startIcon={<RestoreFromTrash />} onClick={handleRestoreBatch}>
                Restaurar Selecionados
              </Button>
              <Button size="small" onClick={() => setSelectedDeleted([])}>Cancelar</Button>
            </Paper>
          )}
          <TableContainer component={Paper}>
            {loadingDeleted ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress size={28} /></Box>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={selectedDeleted.length > 0 && selectedDeleted.length < deletedEquipamentos.length}
                        checked={deletedEquipamentos.length > 0 && selectedDeleted.length === deletedEquipamentos.length}
                        onChange={e => setSelectedDeleted(e.target.checked ? deletedEquipamentos.map(d => d.id) : [])}
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Nome</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Tipo</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Piso</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Local</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Removido em</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Removido por</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Restaurar</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {deletedEquipamentos.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 5, color: 'text.secondary' }}>Nenhum equipamento excluído.</TableCell>
                    </TableRow>
                  ) : deletedEquipamentos.map(eq => (
                    <TableRow key={eq.id} hover selected={selectedDeleted.includes(eq.id)} sx={{ bgcolor: 'rgba(244,67,54,0.04)' }}>
                      <TableCell padding="checkbox">
                        <Checkbox checked={selectedDeleted.includes(eq.id)} onChange={() => handleSelectDeleted(eq.id)} />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 500, color: 'text.secondary' }}>{eq.nome}</TableCell>
                      <TableCell sx={{ color: 'text.secondary' }}>{eq.tipo_equipamento || '—'}</TableCell>
                      <TableCell>{eq.piso ? <Chip label={eq.piso} size="small" variant="outlined" sx={{ fontSize: 11 }} /> : '—'}</TableCell>
                      <TableCell sx={{ color: 'text.secondary' }}>{eq.local || '—'}</TableCell>
                      <TableCell sx={{ fontSize: 12, color: 'text.secondary', whiteSpace: 'nowrap' }}>{fmtDate(eq.deleted_at)}</TableCell>
                      <TableCell>
                        {eq.deleted_by_name
                          ? <Chip label={eq.deleted_by_name} size="small" variant="outlined" color="error" sx={{ fontSize: 11 }} />
                          : <Typography variant="caption" color="text.disabled">—</Typography>}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Restaurar equipamento">
                          <IconButton size="small" color="success" onClick={() => handleRestoreSingle(eq.id)}>
                            <RestoreFromTrash fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </TableContainer>
          <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'text.secondary' }}>
            {deletedEquipamentos.length} equipamento(s) na lixeira
          </Typography>
        </>
      )}

      {/* ══ ABAS NORMAIS ══ */}
      {!isExcluidos && (
        <>
          {/* Filters */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
              {activeTab === 'todos' && (
                <FormControl size="small" sx={{ minWidth: 160, flex: '1 1 150px' }}>
                  <InputLabel>Cliente</InputLabel>
                  <Select value={filters.cliente_id} label="Cliente"
                    onChange={e => setFilters(f => ({ ...f, cliente_id: e.target.value }))}>
                    <MenuItem value="">Todos</MenuItem>
                    {clientes.map(c => <MenuItem key={c.id} value={c.id}>{c.nome}</MenuItem>)}
                  </Select>
                </FormControl>
              )}
              <FormControl size="small" sx={{ minWidth: 170, flex: '1 1 150px' }}>
                <InputLabel>Piso</InputLabel>
                <Select value={filters.piso} label="Piso"
                  onChange={e => setFilters(f => ({ ...f, piso: e.target.value }))}>
                  <MenuItem value="">Todos</MenuItem>
                  {(filterOpts.pisos || []).map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 200, flex: '2 1 180px' }}>
                <InputLabel>Local</InputLabel>
                <Select value={filters.local} label="Local"
                  onChange={e => setFilters(f => ({ ...f, local: e.target.value }))}>
                  <MenuItem value="">Todos</MenuItem>
                  {(filterOpts.locais || []).map(l => <MenuItem key={l} value={l}>{l}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 160, flex: '1 1 150px' }}>
                <InputLabel>Tipo</InputLabel>
                <Select value={filters.tipo_equipamento} label="Tipo"
                  onChange={e => setFilters(f => ({ ...f, tipo_equipamento: e.target.value }))}>
                  <MenuItem value="">Todos</MenuItem>
                  {(filterOpts.tipos || []).map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 150, flex: '1 1 140px' }}>
                <InputLabel>Status</InputLabel>
                <Select value={filters.status} label="Status"
                  onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}>
                  <MenuItem value="">Todos</MenuItem>
                  {STATUS_OPTIONS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </Select>
              </FormControl>
              <TextField size="small" label="Buscar" value={filters.q}
                sx={{ minWidth: 200, flex: '2 1 180px' }}
                onChange={e => setFilters(f => ({ ...f, q: e.target.value }))}
                InputProps={{ startAdornment: <Search sx={{ color: 'text.secondary', mr: 0.5, fontSize: 18 }} /> }}
              />
            </Box>
          </Paper>

          {/* Batch action bar */}
          {selected.length > 0 && (
            <Paper sx={{ p: 1.5, mb: 2, bgcolor: '#e3f2fd', display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Typography fontWeight={600} color="primary">{selected.length} selecionado(s)</Typography>
              <Button size="small" variant="outlined" startIcon={<Person />}
                onClick={() => { setBatchDialog('cliente'); setBatchValue(''); }}>
                {activeTab !== 'todos' ? 'Remover do Cliente' : 'Adicionar a Cliente'}
              </Button>
              <Button size="small" variant="outlined" startIcon={<SettingsIcon />}
                onClick={() => { setBatchDialog('status'); setBatchValue('Ativo'); }}>
                Alterar Status
              </Button>
              {isAdmin() && (
                <Tooltip title={
                  activeTab === 'todos' && selectedCanDelete.length === 0
                    ? 'Todos os selecionados têm cliente associado.'
                    : activeTab === 'todos' && selectedBlocked.length > 0
                      ? `${selectedBlocked.length} item(s) com cliente serão ignorados.`
                      : ''
                }>
                  <span>
                    <Button
                      size="small" variant="outlined" color="error"
                      startIcon={activeTab !== 'todos' ? <LinkOff /> : <Delete />}
                      onClick={() => setDeleteOpen(true)}
                      disabled={activeTab === 'todos' && selectedCanDelete.length === 0}
                    >
                      {activeTab !== 'todos' ? 'Remover do Cliente' : 'Excluir em Lote'}
                    </Button>
                  </span>
                </Tooltip>
              )}
              <Button size="small" onClick={() => setSelected([])}>Cancelar</Button>
            </Paper>
          )}

          {/* Table */}
          <TableContainer component={Paper}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress size={28} /></Box>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={selected.length > 0 && selected.length < displayEquipamentos.length}
                        checked={displayEquipamentos.length > 0 && selected.length === displayEquipamentos.length}
                        onChange={handleSelectAll}
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Código BIM</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Nome</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Tipo</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Piso</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Local</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Clientes</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayEquipamentos.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                        {activeCliente
                          ? `Nenhum equipamento atribuído a "${activeCliente.nome}".`
                          : 'Nenhum equipamento encontrado.'}
                      </TableCell>
                    </TableRow>
                  ) : displayEquipamentos.map(eq => (
                    <TableRow key={eq.id} hover selected={selected.includes(eq.id)}>
                      <TableCell padding="checkbox">
                        <Checkbox checked={selected.includes(eq.id)} onChange={() => handleSelect(eq.id)} />
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 12, color: 'text.secondary' }}>
                        {eq.codigo_bim || '—'}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>{eq.nome}</TableCell>
                      <TableCell>{eq.tipo_equipamento || '—'}</TableCell>
                      <TableCell>
                        {eq.piso ? <Chip label={eq.piso} size="small" variant="outlined" sx={{ fontSize: 11 }} /> : '—'}
                      </TableCell>
                      <TableCell>{eq.local || '—'}</TableCell>
                      <TableCell>
                        {eq.cliente_nomes?.length > 0 ? (
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {eq.cliente_nomes.map((nome, i) => (
                              <Chip key={i} label={nome} size="small" color="primary" variant="outlined" sx={{ fontSize: 11 }} />
                            ))}
                          </Box>
                        ) : (
                          <Typography variant="caption" color="text.disabled">—</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip label={eq.status || 'Ativo'} size="small" color={statusColor(eq.status)} />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => openEdit(eq)}><Edit fontSize="small" /></IconButton>
                        </Tooltip>
                        {isAdmin() && (
                          activeTab !== 'todos' ? (
                            <Tooltip title="Remover do cliente">
                              <IconButton size="small" color="error" onClick={() => handleDeleteSingle(eq.id)}>
                                <LinkOff fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : eq.cliente_ids?.length > 0 ? (
                            <Tooltip title={`Atribuído a: ${eq.cliente_nomes?.join(', ')}. Remova o cliente antes de excluir.`}>
                              <span>
                                <IconButton size="small" color="error" disabled>
                                  <Delete fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          ) : (
                            <Tooltip title="Excluir (lixeira)">
                              <IconButton size="small" color="error" onClick={() => handleDeleteSingle(eq.id)}>
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </TableContainer>
          <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'text.secondary' }}>
            {displayEquipamentos.length} equipamento(s){activeCliente ? ` — ${activeCliente.nome}` : ''}
          </Typography>
        </>
      )}

      {/* ══ MODAIS ══ */}

      {/* Add/Edit */}
      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{editId ? 'Editar Equipamento' : 'Novo Equipamento'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            {FORM_FIELDS.map(({ label, key, sm }) => (
              <Grid item xs={12} sm={sm} key={key}>
                <TextField size="small" fullWidth label={label} value={formData[key]} onChange={setField(key)} />
              </Grid>
            ))}
            {/* Multi-select clientes */}
            <Grid item xs={12} sm={6}>
              <FormControl size="small" fullWidth>
                <InputLabel shrink>Clientes</InputLabel>
                <Select
                  multiple
                  notched
                  displayEmpty
                  value={formData.cliente_ids}
                  label="Clientes"
                  onChange={e => setFormData(f => ({ ...f, cliente_ids: e.target.value }))}
                  renderValue={(sel) =>
                    sel.length === 0
                      ? <Typography variant="body2" color="text.disabled">Sem cliente</Typography>
                      : (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {sel.map(id => (
                            <Chip key={id} size="small"
                              label={clientes.find(c => c.id === id || Number(c.id) === Number(id))?.nome || id}
                            />
                          ))}
                        </Box>
                      )
                  }
                >
                  {clientes.map(c => (
                    <MenuItem key={c.id} value={c.id}>
                      <Checkbox checked={formData.cliente_ids.some(id => Number(id) === Number(c.id))} />
                      <ListItemText primary={c.nome} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl size="small" fullWidth>
                <InputLabel shrink>Status</InputLabel>
                <Select notched value={formData.status} label="Status" onChange={setField('status')}>
                  {STATUS_OPTIONS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField size="small" fullWidth multiline rows={2} label="Observações"
                value={formData.observacoes} onChange={setField('observacoes')} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving || !formData.nome.trim()}>
            {saving ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Batch — Adicionar a Cliente */}
      <Dialog open={batchDialog === 'cliente'} onClose={() => setBatchDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{activeTab !== 'todos' ? 'Remover Cliente em Lote' : 'Adicionar a Cliente em Lote'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {activeTab !== 'todos'
              ? `Remover o cliente "${activeCliente?.nome}" de ${selected.length} equipamento(s).`
              : `Adicionar um cliente a ${selected.length} equipamento(s) selecionado(s).`}
          </Typography>
          {activeTab === 'todos' && (
            <FormControl size="small" fullWidth>
              <InputLabel shrink>Cliente</InputLabel>
              <Select notched displayEmpty value={batchValue} label="Cliente"
                onChange={e => setBatchValue(e.target.value)}
                renderValue={(v) => v ? (clientes.find(c => String(c.id) === String(v))?.nome || v) : 'Selecione um cliente'}>
                {clientes.map(c => <MenuItem key={c.id} value={c.id}>{c.nome}</MenuItem>)}
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBatchDialog(null)}>Cancelar</Button>
          <Button variant="contained" onClick={handleBatchUpdate}
            disabled={activeTab === 'todos' && !batchValue}>
            Aplicar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Batch — Alterar Status */}
      <Dialog open={batchDialog === 'status'} onClose={() => setBatchDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Alterar Status em Lote</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Aplicar para {selected.length} equipamento(s) selecionado(s).
          </Typography>
          <FormControl size="small" fullWidth>
            <InputLabel>Status</InputLabel>
            <Select value={batchValue} label="Status" onChange={e => setBatchValue(e.target.value)}>
              {STATUS_OPTIONS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBatchDialog(null)}>Cancelar</Button>
          <Button variant="contained" onClick={handleBatchUpdate}>Aplicar</Button>
        </DialogActions>
      </Dialog>

      {/* Batch — Confirmar Exclusão */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle color="error.main">{activeTab !== 'todos' ? 'Remover do Cliente' : 'Mover para Lixeira'}</DialogTitle>
        <DialogContent>
          {activeTab !== 'todos' ? (
            <Typography>
              Remover <strong>{selected.length}</strong> equipamento(s) do cliente "{activeCliente?.nome}"?
              (Continuarão em "Todos")
            </Typography>
          ) : (
            <>
              <Typography>
                Mover <strong>{selectedCanDelete.length}</strong> equipamento(s) para a lixeira?
              </Typography>
              {selectedBlocked.length > 0 && (
                <Alert severity="warning" sx={{ mt: 1.5 }}>
                  <strong>{selectedBlocked.length} item(s) ignorado(s)</strong> pois têm cliente associado.
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={handleBatchDelete}>
            {activeTab !== 'todos' ? 'Remover' : 'Mover para Lixeira'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
